import React from 'react';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ItemAvailabilityPointDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useChartColors } from '../useChartColors';
import { formatDate, formatQty } from '../format';
import { SectionCard } from './SectionCard';

interface ItemAvailabilitySectionProps {
  points: ItemAvailabilityPointDto[];
}

const shortDate = (d: string) =>
  new Date(d).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });

/**
 * Lagersjef: disponibelt over tid — fysisk saldo i dag, minus ordrebehov og pluss innkommende
 * per leveringsdato (beregnet i ItemOverviewService.BuildAvailability). Én serie: ingen legend,
 * tittelen navngir den. Nullinjen markerer når varen går tom.
 */
export const ItemAvailabilitySection: React.FC<ItemAvailabilitySectionProps> = ({ points }) => {
  const { t } = useTranslation();
  const colors = useChartColors();

  const firstNegative = points.find(p => p.projected < 0);
  const hasFuture = points.length > 1;
  // Første punkt er i dag: forfalte linjer og linjer uten leveringsdato er lagt hit (BuildAvailability).
  const dueNow = points[0] && (points[0].incoming || points[0].outgoing) ? points[0] : null;

  return (
    <SectionCard
      icon={<TrendingUp className="h-4 w-4" />}
      title={t('warehouse.modules.itemCard.availability.title', {}, 'Disponibelt fremover')}
    >
      {firstNegative && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-red-50 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {t('warehouse.modules.itemCard.availability.runsOut', { '0': formatDate(firstNegative.date), '1': formatQty(firstNegative.projected) },
              `Går tom ${formatDate(firstNegative.date)} (${formatQty(firstNegative.projected)})`)}
          </span>
        </div>
      )}

      {dueNow && (
        <p className="text-sm text-gray-700 mb-3">
          {t('warehouse.modules.itemCard.availability.dueNow',
            { '0': formatQty(dueNow.incoming), '1': formatQty(dueNow.outgoing), '2': formatQty(dueNow.projected) },
            `Forfalt eller uten leveringsdato: +${formatQty(dueNow.incoming)} inn, −${formatQty(dueNow.outgoing)} ut → ${formatQty(dueNow.projected)} disponibelt i dag`)}
        </p>
      )}

      {!hasFuture ? (
        !dueNow && (
          <p className="text-sm text-gray-500 py-2">
            {t('warehouse.modules.itemCard.availability.noFuture', {}, 'Ingen åpne ordre eller innkjøp — disponibelt er lik saldo i dag.')}
          </p>
        )
      ) : (
        <div className="h-56 sm:h-64" role="img" aria-label={t('warehouse.modules.itemCard.availability.title', {}, 'Disponibelt fremover')}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: colors.axisText, fontSize: 11 }} stroke={colors.grid} />
              <YAxis tickFormatter={(v: number) => formatQty(v)} tick={{ fill: colors.axisText, fontSize: 11 }} stroke={colors.grid} width={64} />
              <ReferenceLine y={0} stroke={colors.negative} strokeDasharray="4 4" />
              <Tooltip
                contentStyle={{ background: colors.surface, borderColor: colors.grid, color: colors.text, fontSize: 12, borderRadius: 8 }}
                itemStyle={{ color: colors.text }}
                labelFormatter={(d: string) => formatDate(d)}
                formatter={(value: number | undefined, _name, item) => {
                  const p = item.payload as ItemAvailabilityPointDto;
                  const parts = [formatQty(value)];
                  if (p.incoming) parts.push(`+${formatQty(p.incoming)} ${t('warehouse.modules.itemCard.availability.in', {}, 'inn')}`);
                  if (p.outgoing) parts.push(`−${formatQty(p.outgoing)} ${t('warehouse.modules.itemCard.availability.out', {}, 'ut')}`);
                  return [parts.join(' · '), t('warehouse.modules.itemCard.availability.projected', {}, 'Disponibelt')];
                }}
              />
              <Line
                type="stepAfter"
                dataKey="projected"
                stroke={colors.series}
                strokeWidth={2}
                dot={{ r: 4, fill: colors.series, stroke: colors.surface, strokeWidth: 2 }}
                activeDot={{ r: 6, stroke: colors.surface, strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
};
